import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Shield, ShieldOff, AlertTriangle, Zap, Database, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminCostMonitor = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/cost-monitor/summary?days=7`, auth);
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cost monitor');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleShield = async () => {
    if (!summary) return;
    const currentlyActive = summary.cost_shield_active;
    if (currentlyActive) {
      if (!window.confirm('Lift the cost shield? Low-priority AI tasks (press drafts, blog drafts, city guides) will resume immediately.')) return;
    } else {
      if (!window.confirm('Engage the cost shield? All low-priority AI tasks will pause until you lift it. Revenue-critical features (concierge, pulse-grade, growth-analysis) still run.')) return;
    }
    setToggling(true);
    try {
      const endpoint = currentlyActive ? 'deactivate' : 'activate';
      await axios.post(
        `${API_URL}/api/admin/cost-monitor/shield/${endpoint}`,
        { reason: currentlyActive ? 'Lifted by admin' : 'Engaged by admin to protect credit balance' },
        auth,
      );
      toast.success(`Cost shield ${currentlyActive ? 'lifted' : 'engaged'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Shield toggle failed');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="premium-theme p-10 text-center" data-testid="cost-monitor-loading">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--pe-gold)' }} />
      </div>
    );
  }
  if (!summary) return null;

  const totals = summary.totals || {};
  const shieldActive = !!summary.cost_shield_active;
  const perTask = summary.per_task || [];
  const overAlert = perTask.filter(t => t.est_credits >= summary.thresholds.single_sequence_alert);

  return (
    <div className="premium-theme p-5 sm:p-6 space-y-5" data-testid="admin-cost-monitor" style={{ background: 'var(--pe-bg)', minHeight: '100vh', marginInline: 'calc(-1 * var(--pe-space-md))' }}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="pe-label mb-1">Credit Preservation Protocol</p>
          <h2 className="pe-h2">Cost Monitor</h2>
          <p className="pe-body mt-1" style={{ maxWidth: '640px' }}>
            Real-time LLM spend, cache efficiency and shield status across every AI surface in Shopperden.
          </p>
        </div>
        <button type="button" onClick={load} className="pe-btn pe-btn-ghost" data-testid="refresh-cost-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Shield Status Card */}
      <div
        className="pe-card flex flex-wrap items-start gap-4"
        style={{ borderColor: shieldActive ? 'var(--pe-warning)' : 'var(--pe-gold-muted)', padding: 'var(--pe-space-lg)' }}
        data-testid="cost-shield-card"
      >
        {shieldActive ? (
          <Shield className="h-8 w-8 flex-shrink-0" style={{ color: 'var(--pe-warning)' }} />
        ) : (
          <ShieldOff className="h-8 w-8 flex-shrink-0" style={{ color: 'var(--pe-gold)' }} />
        )}
        <div className="flex-1 min-w-[220px]">
          <p className="pe-label" style={{ color: shieldActive ? 'var(--pe-warning)' : 'var(--pe-gold)' }}>
            Cost Shield · {shieldActive ? 'ENGAGED' : 'STANDBY'}
          </p>
          <h3 className="pe-h3 mt-1">
            {shieldActive ? 'Low-priority AI tasks paused' : 'All AI tasks running normally'}
          </h3>
          {summary.cost_shield_reason && (
            <p className="pe-body mt-1" style={{ fontStyle: 'italic', fontSize: '0.75rem' }} data-testid="shield-reason">
              Reason: {summary.cost_shield_reason}
            </p>
          )}
          <p className="pe-body mt-3" style={{ fontSize: '0.75rem', color: 'var(--pe-text-muted)' }}>
            Auto-engages when estimated daily spend exceeds <strong style={{ color: 'var(--pe-text)' }}>{summary.thresholds.daily_balance_cutoff}</strong> credits.
            Per-sequence alert at <strong style={{ color: 'var(--pe-text)' }}>{summary.thresholds.single_sequence_alert}</strong> credits.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleShield}
          disabled={toggling}
          className={shieldActive ? 'pe-btn pe-btn-destructive' : 'pe-btn pe-btn-primary'}
          data-testid="shield-toggle-btn"
        >
          {toggling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : shieldActive ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
          {shieldActive ? 'Lift Shield' : 'Engage Shield'}
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cost-kpis">
        <div className="pe-kpi">
          <p className="pe-kpi-label">LLM Calls (7d)</p>
          <p className="pe-kpi-value" data-testid="kpi-calls">{totals.calls || 0}</p>
        </div>
        <div className="pe-kpi">
          <p className="pe-kpi-label">Cache Hits (7d)</p>
          <p className="pe-kpi-value pe-kpi-value-ok" data-testid="kpi-cache-hits">{totals.cache_hits || 0}</p>
        </div>
        <div className="pe-kpi">
          <p className="pe-kpi-label">Cache Hit Rate</p>
          <p className="pe-kpi-value pe-kpi-value-gold" data-testid="kpi-hit-rate">{totals.cache_hit_rate_pct || 0}%</p>
        </div>
        <div className="pe-kpi">
          <p className="pe-kpi-label">Est. Credits (7d)</p>
          <p className={`pe-kpi-value ${(totals.est_credits || 0) > 25 ? 'pe-kpi-value-warn' : ''}`} data-testid="kpi-credits">
            ~{(totals.est_credits || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Alert banner */}
      {overAlert.length > 0 && (
        <div className="pe-card flex items-start gap-3" style={{ borderColor: 'var(--pe-error)', background: 'rgba(239, 68, 68, 0.06)' }} data-testid="cost-alert-banner">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--pe-error)' }} />
          <div>
            <p className="pe-body" style={{ color: 'var(--pe-error)', fontWeight: 700 }}>
              Alert: {overAlert.length} task(s) exceeded the {summary.thresholds.single_sequence_alert}-credit sequence threshold this week.
            </p>
            <p className="pe-body mt-1" style={{ fontSize: '0.75rem' }}>
              {overAlert.map(t => `${t.task} (~${t.est_credits.toFixed(2)} credits)`).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* Per-task breakdown */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-lg)' }} data-testid="cost-per-task-table">
        <div className="flex items-center gap-2 mb-4">
          <Database className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
          <h3 className="pe-h3" style={{ fontSize: '1rem' }}>Per-task breakdown (last 7 days)</h3>
        </div>
        {perTask.length === 0 ? (
          <p className="pe-body" style={{ fontStyle: 'italic', color: 'var(--pe-text-muted)' }}>No AI calls recorded in the last 7 days.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="pe">
              <thead>
                <tr>
                  <th>Task</th>
                  <th style={{ textAlign: 'right' }}>LLM Calls</th>
                  <th style={{ textAlign: 'right' }}>Cache Hits</th>
                  <th style={{ textAlign: 'right' }}>Est. Credits</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {perTask.map(t => {
                  const overSeq = t.est_credits >= summary.thresholds.single_sequence_alert;
                  return (
                    <tr key={t.task} data-testid={`task-row-${t.task}`}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.task}</td>
                      <td style={{ textAlign: 'right' }}>{t.calls}</td>
                      <td style={{ textAlign: 'right', color: 'var(--pe-success)', fontWeight: 600 }}>{t.cache_hits}</td>
                      <td style={{ textAlign: 'right', color: overSeq ? 'var(--pe-error)' : 'var(--pe-text)', fontWeight: 600 }}>
                        ~{t.est_credits.toFixed(3)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={overSeq ? 'pe-chip pe-chip-error' : 'pe-chip pe-chip-ok'}>
                          {overSeq ? 'Alert' : 'OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-lg)' }} data-testid="cost-help">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
          <b className="pe-body" style={{ color: 'var(--pe-text)' }}>How the Token-Efficiency Protocol saves credits</b>
        </div>
        <ul className="pe-body" style={{ paddingLeft: '1.25rem', listStyle: 'disc', lineHeight: 1.7 }}>
          <li>Every AI-drafted artefact (press release, blog, city guide) is content-hashed. Re-drafts within TTL return <b style={{ color: 'var(--pe-text)' }}>instantly from cache · 0 credits</b>.</li>
          <li>Concierge replies use the cheapest routable model with a 60-word output cap and canned-fallback if the shield is engaged.</li>
          <li>When daily spend crosses <b style={{ color: 'var(--pe-text)' }}>{summary.thresholds.daily_balance_cutoff} credits</b>, the shield auto-engages.</li>
          <li>Per-sequence alert fires at &gt;<b style={{ color: 'var(--pe-text)' }}>{summary.thresholds.single_sequence_alert} credits</b>.</li>
        </ul>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3" data-testid="cost-actions">
        <button type="button" onClick={load} className="pe-btn pe-btn-ghost">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh summary
        </button>
        <a href="https://app.emergent.sh/profile/universal-key" target="_blank" rel="noopener noreferrer" className="pe-btn pe-btn-primary">
          <TrendingDown className="h-3.5 w-3.5" /> Top up Universal Key
        </a>
      </div>
    </div>
  );
};

export default AdminCostMonitor;
